import {$t} from "@/i18n";


export const TABLE_OPTIONS_KEYS = Object.freeze([
    /**
     * Url parameters that v-data-table uses
     */

    "page",
    "itemsPerPage",
    "sortBy",
    "sortDesc",
    "multiSort",
    "mustSort",
]);

export const filters = [
    /**
     *  Master collection of filters.
     *
     *  "key" - A unique identifier for a filter
     *  "deselect" - If the option is deselectable
     *  "multi" - If multiple items can be selected. Will be tilde-delimited in url.
     *  "items" - Available options. Must be array of objects.
     *   notice :: items value must be as string: {name: $t("No"), value: "0"},
     *  "valueKey" - The key that will be used as name on the filter component
     *  "nameKey" - The key that will be used as value on the filter component and query parameter
     *  "date" - If it is a date selection menu
     *  "prefix" - If the filter component should have a prefix eg. "Period is ..."
     *  "hasActivator" - If the component should render a FilterSelectButton activator
     *  "replaceLabelWithValue" - If the filter button label should be replaced by its value
     *  "delimiter" - Multi value delimiter in the query string. "~" by default
     *
     */
    {
        label: $t("Account"), // Account
        multi: false,
        replaceLabelWithValue: false,
        int: true,
        prefix: true,
        items: [],
        key: "account_id",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Briefcase",
        nameKey: "name",
        valueKey: "id",
        default: false,
        value: null
    },
    {
        label: $t("Account"), // All accounts
        multi: true,
        delimiter: ",",
        int: true,
        deselect: true,
        searchable: false,
        replaceLabelWithValue: false,
        prefix: true,
        items: [],
        key: "account_id_list",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Briefcase",
        nameKey: "name",
        valueKey: "id",
        default: false,
        value: null
    },
    {
        label: $t("Client"),
        multi: true,
        delimiter: ",",
        int: true,
        deselect: true,
        searchable: true,
        prefix: true,
        items: [],
        avatars: true,
        key: "client_id_list",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Contact",
        nameKey: "name",
        valueKey: "id",
        default: false,
        value: null
    },
    {
        label: $t("Client group"),
        multi: true,
        delimiter: ",",
        int: true,
        deselect: true,
        searchable: true,
        items: [],
        prefix: true,
        key: "client_group_id_list",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Contact",
        nameKey: "name",
        valueKey: "id",
        default: false,
        value: null
    },
    {
        label: $t("Topic"),
        multi: true,
        delimiter: ",",
        deselect: true,
        int: true, // fixme :: topic refactor
        searchable: true,
        prefix: true,
        items: [],
        key: "topic_id_list",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "AlignVerticalSpaceAround",
        nameKey: "name",
        valueKey: "id",
        default: false,
        value: null
    },
    {
        label: $t("Member"),
        multi: true,
        delimiter: ",",
        int: true,
        deselect: true,
        replaceLabelWithValue: false,
        searchable: true,
        prefix: true,
        items: [],
        avatars: true,
        key: "person_id_list",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "User",
        nameKey: "name",
        valueKey: "id",
        default: false,
        value: null
    },

    {
        label: $t("Primary member"),
        multi: true,
        delimiter: ",",
        int: true,
        deselect: true,
        replaceLabelWithValue: false,
        searchable: true,
        prefix: true,
        items: [],
        avatars: true,
        key: "default_person_id_list",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "User",
        nameKey: "name",
        valueKey: "id",
        default: false,
        value: null
    },
    {
        label: $t("Client status"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("Active clients"), value: "active"},
            {name: $t("Inactive clients"), value: "inactive"},
            // {name: $t("All"), value: ""},
        ],
        key: "client_status",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Contact",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },
    {
        label: $t("Project status"),
        multi: true,
        delimiter: ",",
        deselect: true,
        replaceLabelWithValue: false,
        prefix: false,
        items: [
            {name: $t("Active"), value: "1"},
            {name: $t("Cancelled"), value: "2"},
            {name: $t("Archived"), value: "4"},
        ],
        key: "project_status",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "ClipboardList",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },
    {
        label: $t("Member status"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("Active members"), value: "active"},
            {name: $t("Limited members"), value: "limited"},
            {name: $t("Inactive members"), value: "inactive"},
            // {name: $t("All"), value: ""},
        ],
        key: "person_status",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "User",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },
    {
        label: $t("Contract status"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("Active contracts"), value: "active"},
            {name: $t("Pending contracts"), value: "pending"},
            {name: $t("Finished contracts"), value: "finished"},
        ],
        key: "contract_status",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "CheckCircle",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null,
    },
    {
        label: $t("Contract template"),
        multi: false,
        deselect: true,
        replaceLabelWithValue: false,
        prefix: true,
        delimiter: ",",
        items: [],
        key: "contract_template_id",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "ClipboardCopy",
        nameKey: "template_name",
        valueKey: "id",
        default: false,
        value: null,
    },
    {
        label: $t("Billing"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: false,
        prefix: true,
        items: [
            {name: $t("Active"), value: "1"},
            {name: $t("No"), value: "0"},
        ],
        key: "is_billable",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Euro",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },
    {
        label: $t("Payment recurrence"),
        deselect: true,
        icon: "CreditCard",
        replaceLabelWithValue: true,
        nameKey: "name",
        valueKey: "value",
        items: [
            {name: $t("Yearly"), value: "yearly"},
            {name: $t("Monthly"), value: "monthly"},
            {name: $t("Trial"), value: "trial"}
        ],
        key: "payment_method",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        default: false,
        value: null
    },
    {
        key: "region_code",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        label: $t("Region"),
        deselect: true,
        icon: "Globe",
        replaceLabelWithValue: true,
        nameKey: "name",
        valueKey: "code",
        items: [
            {name: "Estonia", code: "ee"},
            {name: "UK", code: "gb"},
            {name: "Poland", code: "pl"},
            {name: "USA", code: "us"},
            {name: "EU", code: "eu"},
        ],
        default: false,
        value: null
    },
    {
        key: "country_code",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        label: $t("Country"),
        deselect: true,
        icon: "Globe",
        replaceLabelWithValue: true,
        nameKey: "name",
        valueKey: "code",
        items: [
            {name: "United Kingdom", code: "gb"},
            {name: "Estonia", code: "ee"}
        ],
        default: false,
        value: null
    },
    {
        key: "area_id",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        label: $t("Field"),
        deselect: true,
        icon: "Briefcase",
        replaceLabelWithValue: true,
        nameKey: "name_key",
        valueKey: "id",
        items: [],
        default: false,
        value: null
    },

    {
        label: $t("Start date"), // "Task status"// label: $t("All tasks"), // "Task status"
        multi: false,
        deselect: true,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("Todo"), value: "todo"}, // { name: $t("Not done"), value: "todo" },
            {name: $t("Overdue"), value: "overdue"},
            {name: $t("Done"), value: "done"},
            {name: $t("Delayed"), value: "delayed"},
            {name: $t("All tasks"), value: ""}, //{ name: $t("All tasks"), value: "" },
        ],
        key: "task_status",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "CheckCircle",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },

    {
        key: "is_important",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        label: $t("Important"),
        deselect: true,
        hideMenu: true,
        icon: "Flag",
        replaceLabelWithValue: true,
        nameKey: "name",
        valueKey: "value",
        prefix: false,
        items: [
            {name: $t("Important"), value: "1"},
        ],
        default: false,
        value: null
    },
    {
        key: "is_extra",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        label: $t("Extra work"),
        deselect: true,
        icon: "BookmarkPlus",
        hideMenu: true,
        replaceLabelWithValue: true,
        nameKey: "name",
        valueKey: "value",
        prefix: false,
        items: [
            {name: $t("Extra work"), value: "1"},
        ],
        default: false,
        value: null
    },
    {
        label: $t("I Follow"),
        deselect: true,
        icon: "Bell",
        replaceLabelWithValue: true,
        hideMenu: true,
        nameKey: "name",
        valueKey: "value",
        prefix: false,
        items: [
            {name: $t("I Follow"), value: "1"},
        ],
        key: "follow",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        default: false,
        value: null
    },
    {
        label: $t("Commented"),
        deselect: true,
        icon: "MessageSquare",
        replaceLabelWithValue: true,
        hideMenu: true,
        nameKey: "name",
        valueKey: "value",
        prefix: false,
        items: [
            {name: $t("Commented"), value: "1"},
        ],
        key: "is_commented",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        default: false,
        value: null
    },
    {
        label: $t("Tracked time"),
        deselect: false,
        icon: "PlayCircle",
        replaceLabelWithValue: false,
        hideMenu: false,
        nameKey: "name",
        valueKey: "value",
        prefix: true,
        items: [
            {name: $t("Yes"), value: "1"},
            {name: $t("No"), value: "0"},
        ],
        key: "is_tracked_time",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        default: false,
        value: null
    },
    {
        label: $t("Estimated"),
        deselect: true,
        icon: "Clock4",
        replaceLabelWithValue: true,
        hideMenu: true,
        nameKey: "name",
        valueKey: "value",
        prefix: false,
        items: [
            {name: $t("Estimated"), value: "1"},
        ],
        key: "is_estimated",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        default: false,
        value: null
    },
    {
        label: $t("Recurring task"),
        deselect: true,
        icon: "Calendar",
        replaceLabelWithValue: true,
        hideMenu: true,
        nameKey: "name",
        valueKey: "value",
        prefix: false,
        items: [
            {name: $t("Recurring task"), value: "1"},
        ],
        key: "is_recurring",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        default: false,
        value: null
    },
    {
        label: $t("Monitoring"),
        deselect: false,
        icon: "FileSearch2",
        replaceLabelWithValue: false,
        hideMenu: false,
        nameKey: "name",
        valueKey: "value",
        prefix: true,
        items: [
            {name: $t("Active"), value: "1"},
            {name: $t("No"), value: "0"},
        ],
        key: "is_monitored",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        default: false,
        value: null
    },
    {
        label: $t("Last import"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("Last import"), value: "1"},
        ],
        key: "client_last_import",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Download",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },
    {
        label: $t("Period"),
        date: true,
        icon: "CalendarDays",
        replaceLabelWithValue: true,
        hideCalendars: true,
        key: "dates_list",
        notActive: true,
        component: () => import("../NewFilterBar/components/DateMenu"),
        hasActivator: true,
        default: false,
        value: null
    },

    {
        key: "client_search",
        component: () => import("./components/SearchBox"),
        label: $t("Client search"),
        default: false,
        value: null
    },
    {
        key: "voucher_search",
        component: () => import("./components/SearchBox"),
        label: $t("Voucher"),
        default: false,
        value: null
    },


    {
        label: $t("Company status"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("Active"), value: "active"},
            {name: $t("Inactive"), value: "inactive"},
            {name: $t("Deleted"), value: "deleted"},
            {name: $t("All status"), value: ""},
        ],
        key: "company_status",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "Contact",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },

    {
        label: $t("Payment gateway"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("All gateways"), value: ""},
            {name: $t("Uku manually"), value: "uku"},
            {name: $t("Braintree"), value: "braintree"},
        ],
        key: "payment_gateway",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "CreditCard",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },

    {
        label: $t("Plan status"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("All plans"), value: ""},
            {name: $t("Paid"), value: "is_paid"},
            {name: $t("Paid churn"), value: "is_paid_churn"},
            {name: $t("Trial"), value: "is_trial"},
            {name: $t("Trial churn"), value: "is_trial_churn"},
        ],
        key: "plan_status",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "CreditCard",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },
    {
        label: $t("Plan period"),
        multi: false,
        deselect: false,
        replaceLabelWithValue: true,
        prefix: false,
        items: [
            {name: $t("All periods"), value: ""},
            {name: $t("Monthly plan"), value: "month"},
            {name: $t("Yearly plan"), value: "year"},
        ],
        key: "plan_period",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "CreditCard",
        nameKey: "name",
        valueKey: "value",
        default: false,
        value: null
    },

    {
        key: "company_search",
        component: () => import("./components/SearchBox"),
        label: $t("Company search"),
        default: false,
        value: null
    },
    {
        key: "user_search",
        component: () => import("./components/SearchBox"),
        label: $t("User search"),
        default: false,
        value: null
    },
    {
        key: "name_key_search",
        component: () => import("./components/SearchBox"),
        label: $t("Name key search"),
        default: false,
        value: null
    },
    {
        key: "keyword",
        component: () => import("./components/SearchBox"),
        label: $t("Search"),
        default: false,
        value: null
    },
    {
        key: "is_cp",
        multi: false,
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        label: $t("Client portal"),
        hideMenu: false,
        icon: "Users",
        replaceLabelWithValue: true,
        nameKey: "name",
        valueKey: "value",
        prefix: true,
        items: [
            { name: $t("Yes"), value: "1" },
            { name: $t("No"), value: "0" }
        ],
        default: false,
        canOpen: true,
        value: null
    },
    {
        label: $t("Project"),
        multi: true,
        delimiter: ",",
        deselect: true,
        int: true,
        searchable: true,
        prefix: true,
        items: [],
        key: "project_id_list",
        component: () => import("./components/DropMenu"),
        hasActivator: true,
        icon: "ClipboardList",
        nameKey: "name",
        valueKey: "id",
        default: false,
        canOpen: true
    },
];